import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarOutlined,
  ClearOutlined,
  CopyOutlined,
  LeftOutlined,
  RightOutlined
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  DatePicker,
  Dropdown,
  Empty,
  InputNumber,
  Modal,
  Select,
  Table,
  Tooltip,
  message
} from "antd";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";
import { getWorkplaceNumber } from "@core/runtime-settings";
import {
  fetchNutritionCalc,
  fetchNutritionLookups,
  replaceNutritionCalcDay
} from "./nutrition-api";
import {
  displayDate,
  emptyLookups,
  field,
  isoDate,
  lookupOptions,
  normalizeLookups,
  numberField,
  showError
} from "./nutrition-utils";
import NutritionDrawer, { DrawerFooter } from "./nutrition-drawer";
import mealBreakfastSrc from "@components/doc/exp/assets/meal-breakfast.svg";
import mealLunchSrc from "@components/doc/exp/assets/meal-lunch.svg";
import mealSnackSrc from "@components/doc/exp/assets/meal-snack.svg";
import mealDinnerSrc from "@components/doc/exp/assets/meal-dinner.svg";
import "./nutrition.css";

const DOW_SHORT = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];
const DOW_FULL = [
  "воскресенье",
  "понедельник",
  "вторник",
  "среда",
  "четверг",
  "пятница",
  "суббота"
];

const PART_ICON_SRC = {
  1: mealBreakfastSrc,
  2: mealLunchSrc,
  3: mealSnackSrc,
  4: mealDinnerSrc
};

const PartIcon = ({ part, className = "" }) => {
  const src = PART_ICON_SRC[part];
  return src ? (
    <span
      className={`nutrition-cost-meal-icon ${className}`.trim()}
      style={{ "--meal-icon": `url("${src}")` }}
      aria-hidden
    />
  ) : null;
};

const MONTHS_SHORT = [
  "янв",
  "фев",
  "мар",
  "апр",
  "мая",
  "июн",
  "июл",
  "авг",
  "сен",
  "окт",
  "ноя",
  "дек"
];

const fmtMoney = (value) =>
  Number(value || 0).toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

const partPrice = (day, part, key) =>
  numberField((field(day, "parts", []) || []).find((x) => numberField(x, "part") === part), key);

const partCounts = (day, part) => {
  const row = (field(day, "parts", []) || []).find((x) => numberField(x, "part") === part) || {};
  return { adult: numberField(row, "adultCount"), child: numberField(row, "childCount") };
};

const dayTotals = (day) => {
  let adult = 0;
  let child = 0;
  let voucher = 0;
  (field(day, "parts", []) || []).forEach((p) => {
    adult += numberField(p, "adultPrice");
    child += numberField(p, "childPrice");
    voucher += numberField(p, "voucherAmount");
  });
  return { adult, child, voucher };
};

const dayIsFilled = (day) => {
  const t = dayTotals(day);
  return t.adult > 0 || t.child > 0 || t.voucher > 0;
};

const moneyCell = (value) =>
  Number(value) > 0 ? (
    <span className="nutrition-cost-money">{fmtMoney(value)}</span>
  ) : (
    <span className="nutrition-cost-dash">—</span>
  );

const priceColumn = (day, part, priceKey, countKey) => {
  const value = partPrice(day, part, priceKey);
  const count = partCounts(day, part)[countKey];
  return (
    <span className="nutrition-cost-cell">
      {moneyCell(value)}
      {count > 0 ? <span className="nutrition-cost-count">{count}</span> : null}
    </span>
  );
};

const weekPreset = () => {
  const dow = (dayjs().day() + 6) % 7;
  const start = dayjs().subtract(dow, "day").startOf("day");
  return [start, start.add(6, "day").endOf("day")];
};

const NutritionCost = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState(() => {
    const beg = dayjs(searchParams.get("dateBeg"));
    const end = dayjs(searchParams.get("dateEnd"));
    return beg.isValid() && end.isValid() && !end.isBefore(beg)
      ? [beg.startOf("day"), end.endOf("day")]
      : [dayjs().startOf("month"), dayjs().endOf("month")];
  });
  const [idBranch, setIdBranch] = useState(() => {
    const fromUrl = searchParams.get("idBranch");
    return fromUrl != null && fromUrl !== "" ? Number(fromUrl) || 0 : getWorkplaceNumber("idBranch", 0);
  });

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const isDefaultPeriod =
      period[0].isSame(dayjs().startOf("month"), "day") && period[1].isSame(dayjs().endOf("month"), "day");
    if (isDefaultPeriod) {
      next.delete("dateBeg");
      next.delete("dateEnd");
    } else {
      next.set("dateBeg", period[0].format("YYYY-MM-DD"));
      next.set("dateEnd", period[1].format("YYYY-MM-DD"));
    }
    if (idBranch) next.set("idBranch", String(idBranch));
    else next.delete("idBranch");
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [period, idBranch, searchParams, setSearchParams]);
  const [lookups, setLookups] = useState(emptyLookups);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(null); // { index, date, idBranch, parts, initial }
  const [applyAll, setApplyAll] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    fetchNutritionLookups()
      .then((data) => {
        const normalized = normalizeLookups(data);
        setLookups(normalized);
        if (!normalized.canChooseBranch) {
          setIdBranch((current) => current || getWorkplaceNumber("idBranch", 0));
        }
      })
      .catch((e) => showError(messageApi, e, "Не удалось загрузить справочники"));
  }, [messageApi]);

  const load = useCallback(async () => {
    if (!period?.[0] || !period?.[1]) return;
    setLoading(true);
    try {
      const data = await fetchNutritionCalc(isoDate(period[0]), isoDate(period[1]), idBranch);
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      showError(messageApi, error, "Не удалось рассчитать стоимость");
    } finally {
      setLoading(false);
    }
  }, [period, idBranch, messageApi]);
  useEffect(() => void load(), [load]);

  const partIds = useMemo(
    () => (lookups.parts.length ? lookups.parts : [1, 2, 3, 4].map((id) => ({ id, name: `№ ${id}` })))
      .map((p) => p.id)
      .sort((a, b) => a - b),
    [lookups.parts]
  );

  const partName = useCallback(
    (value) => lookups.parts.find((x) => x.id === Number(value))?.name || `№ ${value}`,
    [lookups.parts]
  );

  const stats = useMemo(() => {
    const filled = rows.filter(dayIsFilled);
    const avg = (key) =>
      filled.length ? filled.reduce((n, d) => n + dayTotals(d)[key], 0) / filled.length : null;
    const persons = rows.reduce(
      (acc, d) => {
        (field(d, "parts", []) || []).forEach((p) => {
          acc.adult += numberField(p, "adultCount");
          acc.child += numberField(p, "childCount");
        });
        return acc;
      },
      { adult: 0, child: 0 }
    );
    return {
      filled: filled.length,
      total: rows.length,
      avgAdult: avg("adult"),
      avgChild: avg("child"),
      avgVoucher: avg("voucher"),
      persons
    };
  }, [rows]);

  const openDay = useCallback(
    (index) => {
      const day = rows[index];
      if (!day) return;
      const parts = partIds.map((part) => ({
        part,
        adultPrice: partPrice(day, part, "adultPrice"),
        childPrice: partPrice(day, part, "childPrice"),
        voucherAmount: partPrice(day, part, "voucherAmount")
      }));
      setEdit({
        index,
        date: field(day, "fdate"),
        idBranch: numberField(day, "idBranch"),
        parts,
        initial: JSON.stringify(parts)
      });
      setApplyAll(false);
    },
    [rows, partIds]
  );

  const dirty = edit ? JSON.stringify(edit.parts) !== edit.initial : false;

  const editPayload = useCallback(
    () => ({
      idBranch: edit?.idBranch || idBranch,
      parts: partIds.map((part) => {
        const row = edit.parts.find((x) => x.part === part) || {};
        return {
          part,
          adultPrice: Number(row.adultPrice) || 0,
          childPrice: Number(row.childPrice) || 0,
          voucherAmount: Number(row.voucherAmount) || 0
        };
      })
    }),
    [edit, partIds, idBranch]
  );

  const saveDay = useCallback(
    async (date) => {
      await replaceNutritionCalcDay(isoDate(date), editPayload());
    },
    [editPayload]
  );

  const save = useCallback(async () => {
    if (!edit) return;
    if (applyAll) {
      Modal.confirm({
        title: `Записать эти цены на ${rows.length} дн. (с ${displayDate(period[0])} по ${displayDate(period[1])})?`,
        okText: "Записать",
        cancelText: "Отмена",
        onOk: async () => {
          setSavingAll(true);
          try {
            for (let i = 0; i < rows.length; i += 1) {
              messageApi.loading({
                key: "nutrition-cost-all",
                content: `Запись дня ${i + 1} из ${rows.length}…`,
                duration: 0
              });
              await saveDay(field(rows[i], "fdate"));
            }
            messageApi.destroy("nutrition-cost-all");
            messageApi.success("Цены записаны на весь период");
            setEdit(null);
            await load();
          } catch (error) {
            messageApi.destroy("nutrition-cost-all");
            showError(messageApi, error, "Не удалось сохранить стоимость");
          } finally {
            setSavingAll(false);
          }
        }
      });
      return;
    }
    setLoading(true);
    try {
      await saveDay(edit.date);
      messageApi.success("Стоимость дня сохранена");
      setEdit(null);
      await load();
    } catch (error) {
      showError(messageApi, error, "Не удалось сохранить стоимость");
    } finally {
      setLoading(false);
    }
  }, [edit, applyAll, rows, period, saveDay, load, messageApi]);

  const gotoDay = useCallback(
    (index) => {
      if (index < 0 || index >= rows.length) return;
      const go = () => openDay(index);
      if (!dirty) {
        go();
        return;
      }
      Modal.confirm({
        title: "Сохранить изменения?",
        okText: "Сохранить",
        cancelText: "Не сохранять",
        onOk: async () => {
          try {
            await saveDay(edit.date);
            messageApi.success("Стоимость дня сохранена");
            await load();
            go();
          } catch (error) {
            showError(messageApi, error, "Не удалось сохранить стоимость");
          }
        },
        onCancel: go
      });
    },
    [dirty, rows.length, openDay, saveDay, edit, load, messageApi]
  );

  const closeEditor = useCallback(() => {
    if (!dirty) {
      setEdit(null);
      return;
    }
    Modal.confirm({
      title: "Сохранить изменения?",
      okText: "Сохранить",
      cancelText: "Не сохранять",
      onOk: async () => {
        try {
          await saveDay(edit.date);
          messageApi.success("Стоимость дня сохранена");
          await load();
          setEdit(null);
        } catch (error) {
          showError(messageApi, error, "Не удалось сохранить стоимость");
        }
      },
      onCancel: () => setEdit(null)
    });
  }, [dirty, edit, saveDay, load, messageApi]);

  const copyPrevDay = useCallback(() => {
    if (!edit || edit.index <= 0) return;
    const prev = rows[edit.index - 1];
    if (!prev || !dayIsFilled(prev)) return;
    setEdit((cur) => ({
      ...cur,
      parts: cur.parts.map((p) => ({
        ...p,
        adultPrice: partPrice(prev, p.part, "adultPrice"),
        childPrice: partPrice(prev, p.part, "childPrice"),
        voucherAmount: partPrice(prev, p.part, "voucherAmount")
      }))
    }));
  }, [edit, rows]);

  const setPartField = useCallback((part, key, value) => {
    setEdit((cur) => ({
      ...cur,
      parts: cur.parts.map((p) => (p.part === part ? { ...p, [key]: value } : p))
    }));
  }, []);

  const todayIso = isoDate(dayjs());

  const columns = useMemo(() => {
    const cols = [
      {
        title: "Дата",
        key: "fdate",
        fixed: "left",
        width: 100,
        render: (_, day) => {
          const d = dayjs(field(day, "fdate"));
          const isToday = isoDate(d) === todayIso;
          return (
            <span className={`nutrition-cost-date${isToday ? " nutrition-cost-date--today" : ""}`}>
              <span className="nutrition-cost-date-main">
                {d.date()} {MONTHS_SHORT[d.month()]}
              </span>
              <span className="nutrition-cost-date-dow">{DOW_SHORT[d.day()]}</span>
            </span>
          );
        }
      },
      ...partIds.map((part) => {
        return {
          title: (
            <span className="nutrition-cost-part-head">
              <PartIcon part={part} />
              {partName(part)}
            </span>
          ),
          children: [
            {
              title: "Взр.",
              key: `p${part}-a`,
              align: "right",
              width: 64,
              className: "nutrition-cost-col--group-start",
              render: (_, day) => priceColumn(day, part, "adultPrice", "adult")
            },
            {
              title: "Реб.",
              key: `p${part}-c`,
              align: "right",
              width: 60,
              render: (_, day) => priceColumn(day, part, "childPrice", "child")
            },
            {
              title: "Путёвка",
              key: `p${part}-v`,
              align: "right",
              width: 60,
              render: (_, day) => moneyCell(partPrice(day, part, "voucherAmount"))
            }
          ]
        };
      }),
      {
        title: "Итого за день",
        children: [
          {
            title: "Взр.",
            key: "total-a",
            align: "right",
            width: 72,
            className: "nutrition-cost-col--group-start",
            render: (_, day) =>
              dayIsFilled(day) ? (
                <span className="nutrition-cost-money nutrition-cost-money--total">
                  {fmtMoney(dayTotals(day).adult)}
                </span>
              ) : (
                <span className="nutrition-cost-dash">—</span>
              )
          },
          {
            title: "Реб.",
            key: "total-c",
            align: "right",
            width: 66,
            render: (_, day) =>
              dayIsFilled(day) ? (
                <span className="nutrition-cost-money nutrition-cost-money--total">
                  {fmtMoney(dayTotals(day).child)}
                </span>
              ) : (
                <span className="nutrition-cost-dash">—</span>
              )
          },
          {
            title: "Путёвка",
            key: "total-v",
            align: "right",
            width: 66,
            render: (_, day) =>
              dayIsFilled(day) ? (
                <span className="nutrition-cost-money nutrition-cost-money--total">
                  {fmtMoney(dayTotals(day).voucher)}
                </span>
              ) : (
                <span className="nutrition-cost-dash">—</span>
              )
          }
        ]
      },
      { key: "filler", title: null, className: "nutrition-cost-col--filler" }
    ];
    return cols;
  }, [partIds, partName, todayIso]);

  const rangePresets = useMemo(
    () => [
      {
        label: "Этот месяц",
        value: [dayjs().startOf("month"), dayjs().endOf("month")]
      },
      {
        label: "Следующий месяц",
        value: [dayjs().add(1, "month").startOf("month"), dayjs().add(1, "month").endOf("month")]
      },
      {
        label: "Прошлый месяц",
        value: [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")]
      },
      { label: "Эта неделя", value: weekPreset() }
    ],
    []
  );

  const prevFilled = edit && edit.index > 0 ? dayIsFilled(rows[edit.index - 1]) : false;

  const editDate = edit ? dayjs(edit.date) : null;
  const editTotals = edit
    ? edit.parts.reduce(
        (acc, p) => ({
          adult: acc.adult + (Number(p.adultPrice) || 0),
          child: acc.child + (Number(p.childPrice) || 0),
          voucher: acc.voucher + (Number(p.voucherAmount) || 0)
        }),
        { adult: 0, child: 0, voucher: 0 }
      )
    : null;

  const onFieldEnter = (index) => {
    if (index + 1 < inputRefs.current.length) {
      inputRefs.current[index + 1]?.focus();
    } else {
      void save();
    }
  };

  return (
    <div className="nutrition-page">
      {contextHolder}
      <div className="nutrition-cost-head">
        <div className="nutrition-cost-filters">
          <Dropdown
            menu={{
              items: rangePresets.map((p) => ({ key: p.label, label: p.label })),
              onClick: ({ key }) => {
                const preset = rangePresets.find((p) => p.label === key);
                if (preset) setPeriod(preset.value);
              }
            }}
          >
            <Button type="text" icon={<CalendarOutlined />} aria-label="Быстрый выбор периода" />
          </Dropdown>
          <DatePicker
            value={period[0]}
            format="DD.MM.YYYY"
            allowClear={false}
            variant="borderless"
            suffixIcon={null}
            className="nutrition-cost-date-input"
            onChange={(d) => d && setPeriod(([, end]) => [d.startOf("day"), end.isBefore(d) ? d.endOf("day") : end])}
          />
          <span className="nutrition-cost-period-label">—</span>
          <DatePicker
            value={period[1]}
            format="DD.MM.YYYY"
            allowClear={false}
            variant="borderless"
            suffixIcon={null}
            minDate={period[0]}
            className="nutrition-cost-date-input"
            onChange={(d) => d && setPeriod(([beg]) => [beg, d.endOf("day")])}
          />
          {lookups.canChooseBranch || lookups.branches.length > 1 ? (
            <>
              <span className="nutrition-cost-filters-sep" />
              <Select
                value={idBranch}
                options={[
                  ...(lookups.canChooseBranch ? [{ value: 0, label: "Все филиалы" }] : []),
                  ...lookupOptions(lookups.branches)
                ]}
                variant="borderless"
                popupMatchSelectWidth={false}
                className="nutrition-cost-branch"
                disabled={!lookups.canChooseBranch}
                onChange={setIdBranch}
              />
            </>
          ) : null}
        </div>

        <div className="nutrition-cost-stats">
        {[
          {
            key: "filled",
            value: `${stats.filled} из ${stats.total}`,
            label: "Заполнено",
            hint: "Дней с заданной стоимостью из всех дней периода",
            cls: stats.filled === stats.total && stats.total ? " nutrition-cost-stat-value--ok" : " nutrition-cost-stat-value--warn"
          },
          {
            key: "adult",
            value: stats.avgAdult == null ? "—" : fmtMoney(stats.avgAdult),
            label: "Взр., ₽",
            hint: "Средняя стоимость дня питания взрослого (по заполненным дням)"
          },
          {
            key: "child",
            value: stats.avgChild == null ? "—" : fmtMoney(stats.avgChild),
            label: "Реб., ₽",
            hint: "Средняя стоимость дня питания ребёнка (по заполненным дням)"
          },
          {
            key: "voucher",
            value: stats.avgVoucher == null ? "—" : fmtMoney(stats.avgVoucher),
            label: "Путёвка, ₽",
            hint: "Средняя сумма питания по путёвке за день (по заполненным дням)"
          },
          {
            key: "persons",
            value: stats.persons.adult + stats.persons.child,
            label: "Чел.-дней",
            hint: `Человеко-дней за период: взрослых ${stats.persons.adult}, детей ${stats.persons.child}`
          }
        ].map((s) => (
          <Tooltip key={s.key} title={s.hint}>
            <div className="nutrition-cost-stat">
              <div className={`nutrition-cost-stat-value${s.cls || ""}`}>{s.value}</div>
              <div className="nutrition-cost-stat-label">{s.label}</div>
            </div>
          </Tooltip>
        ))}
        </div>
      </div>

      <div className="nutrition-table-wrap">
        <Table
          rowKey={(day) => isoDate(field(day, "fdate"))}
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={false}
          size="small"
          sticky
          scroll={{ x: 1040 }}
          tableLayout="fixed"
          rowClassName={(day) => {
            const d = dayjs(field(day, "fdate"));
            return [
              "nutrition-cost-day",
              [0, 6].includes(d.day()) ? "nutrition-cost-day--weekend" : "",
              isoDate(d) === todayIso ? "nutrition-cost-day--today" : "",
              dayIsFilled(day) ? "" : "nutrition-cost-day--empty"
            ]
              .filter(Boolean)
              .join(" ");
          }}
          onRow={(day, index) => ({ onClick: () => openDay(index) })}
          locale={{ emptyText: <Empty description="Нет данных за период" /> }}
        />
      </div>

      <NutritionDrawer
        open={Boolean(edit)}
        title={
          editDate ? (
            <span className="nutrition-cost-drawer-title">
              <Button
                type="text"
                size="small"
                icon={<LeftOutlined />}
                disabled={edit.index <= 0 || savingAll}
                aria-label="Предыдущий день"
                onClick={() => gotoDay(edit.index - 1)}
              />
              <span>
                Стоимость на {editDate.date()} {MONTHS_SHORT[editDate.month()]} {editDate.year()},{" "}
                {DOW_FULL[editDate.day()]}
              </span>
              <Button
                type="text"
                size="small"
                icon={<RightOutlined />}
                disabled={edit.index >= rows.length - 1 || savingAll}
                aria-label="Следующий день"
                onClick={() => gotoDay(edit.index + 1)}
              />
            </span>
          ) : null
        }
        onClose={closeEditor}
        width={720}
      >
        {edit ? (
          <div className="nutrition-cost-editor">
            <div className="nutrition-cost-editor-actions">
              <Tooltip
                title={
                  edit.index <= 0
                    ? "Нет предыдущего дня в периоде"
                    : !prevFilled
                      ? "В предыдущем дне цены не заданы"
                      : ""
                }
              >
                <span>
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    disabled={!prevFilled}
                    onClick={copyPrevDay}
                  >
                    Скопировать с предыдущего дня
                  </Button>
                </span>
              </Tooltip>
              <Button
                size="small"
                icon={<ClearOutlined />}
                onClick={() =>
                  setEdit((cur) => ({
                    ...cur,
                    parts: cur.parts.map((p) => ({
                      ...p,
                      adultPrice: 0,
                      childPrice: 0,
                      voucherAmount: 0
                    }))
                  }))
                }
              >
                Очистить
              </Button>
            </div>
            <div className="nutrition-cost-grid">
              <div className="nutrition-cost-grid-row nutrition-cost-grid-row--head">
                <span />
                <span>Взр.</span>
                <span>Реб.</span>
                <span>Путёвка</span>
              </div>
              {edit.parts.map((p, pIdx) => {
                const counts = partCounts(rows[edit.index] || {}, p.part);
                return (
                  <div key={p.part} className="nutrition-cost-grid-row">
                    <span className="nutrition-cost-grid-part">
                      <PartIcon part={p.part} className="nutrition-cost-grid-icon" />
                      <span>{partName(p.part)}</span>
                      <span className="nutrition-cost-grid-count">
                        взр. {counts.adult} · дет. {counts.child}
                      </span>
                    </span>
                    {["adultPrice", "childPrice", "voucherAmount"].map((key, cIdx) => {
                      const refIndex = pIdx * 3 + cIdx;
                      return (
                        <InputNumber
                          key={key}
                          ref={(el) => {
                            inputRefs.current[refIndex] = el;
                          }}
                          className="nutrition-cost-input"
                          min={0}
                          precision={2}
                          addonAfter="₽"
                          value={p[key]}
                          onChange={(value) => setPartField(p.part, key, value)}
                          onPressEnter={() => onFieldEnter(refIndex)}
                        />
                      );
                    })}
                  </div>
                );
              })}
              <div className="nutrition-cost-grid-row nutrition-cost-grid-row--total">
                <span>Итого</span>
                <span className="nutrition-cost-money nutrition-cost-money--total">
                  {fmtMoney(editTotals.adult)}
                </span>
                <span className="nutrition-cost-money nutrition-cost-money--total">
                  {fmtMoney(editTotals.child)}
                </span>
                <span className="nutrition-cost-money nutrition-cost-money--total">
                  {fmtMoney(editTotals.voucher)}
                </span>
              </div>
            </div>
            <div className="nutrition-cost-drawer-footer">
              <Checkbox
                checked={applyAll}
                onChange={(e) => setApplyAll(e.target.checked)}
                disabled={savingAll}
              >
                Применить ко всем дням периода
              </Checkbox>
              <DrawerFooter onSave={save} onClose={closeEditor} saving={loading || savingAll} />
            </div>
          </div>
        ) : null}
      </NutritionDrawer>
    </div>
  );
};

export default NutritionCost;
